import LoadingBar from 'react-top-loading-bar';
import React, { useRef, useEffect, useState } from 'react';
import { ErrorBoundary } from '../lib/bugsnag';
import '../lib/bugsnagPerformance';
import Image from 'next/image';
import Head from 'next/head';
import { Provider } from 'react-redux';
import '@fortawesome/fontawesome-free/css/all.min.css';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';

import Layout from './v2/Layout';
import { sessionStorageKeys } from '../utils';
import { IgnoredSentryErrors, protectedRoutes } from '../helpers/constants';
import { wrapper, store } from '../store/store';
import AccessDenied from '../components/ErrorPage/AccessDenied';
import loaderGIF from '../public/loader.gif';
import '../styles/globals.css';
import 'antd/dist/antd.css';
import { sessionReplayMixpanel } from '../lib/mixPanel';

function MyApp({ Component, pageProps }) {
    const ref = useRef(null);
    const [isMobile, setIsMobile] = useState(true);
    const [isStandaoloneApp, setIsStandaloneApp] = useState(false);
    const [isUserLoggedIn, setIsUserLoggedIn] = useState(null);
    const hideTopLoader = () => ref.current.complete();
    const showTopLoader = () => ref.current.continuousStart();

    const router = useRouter();

    // Function to check login status
    const checkLoginStatus = () => {
        const accessToken = localStorage.getItem('accessToken');
        setIsUserLoggedIn(!!accessToken);
    };

    useEffect(() => {
        checkLoginStatus();
        window.addEventListener('storage', checkLoginStatus);
        return () => {
            window.removeEventListener('storage', checkLoginStatus);
        };
    }, [router]);

    useEffect(() => {
        sessionReplayMixpanel();
        const isMobile = window.navigator.userAgent.match(
            /(iPad)|(iPhone)|(iPod)|(android)|(webOS)/i
        );
        const isStandaoloneMode = window.matchMedia(
            '(display-mode: standalone)'
        ).matches;
        setIsMobile(isMobile);
        setIsStandaloneApp(isStandaoloneMode);
        sessionStorage.setItem(
            sessionStorageKeys.IS_MOBILE,
            isMobile ? true : false
        );
        // if (process.env.NODE_ENV === 'production') {
        //     Sentry.init({
        //         dsn: 'https://368e1d4daeed495b9142ce85a373c6f2@o1166858.ingest.sentry.io/6343215',
        //         tracesSampleRate: 1.0,
        //         ignoreFile: '.sentryclirc',
        //         ignore: ['node_modules'],
        //         environment: 'production',
        //         release: process.env.NEXT_PUBLIC_RELEASE,
        //         beforeSend(event) {
        //             let rejectEvent;
        //             const { exception: SentryExceptions } = event || {};
        //             (SentryExceptions?.values || [])?.forEach?.((exception) => {
        //                 const { value } = exception || {};
        //                 if (
        //                     IgnoredSentryErrors.some(
        //                         (error) =>
        //                             !!value &&
        //                             error?.message
        //                                 ?.toLowerCase()
        //                                 ?.includes(value?.toLowerCase())
        //                     )
        //                 )
        //                     rejectEvent = true;
        //             });
        //             return rejectEvent ? null : event;
        //         },
        //         ignoreErrors: [
        //             'getReadModeConfig',
        //             'getReadModeExtract',
        //             'getReadModeRender',
        //         ],
        //     });
        // }
    }, []);

    useEffect(() => {
        //disable back button on some routes
        router.beforePopState(({ url, as, options }) => {
            if (
                url.includes('/payment-progress') ||
                url.includes('/upi-landing') ||
                url.includes('/upi-tray') ||
                url.includes('/upi-qr') ||
                url.includes('/payment-status')
            ) {
                if (!alert('You will be redirected to Home screen')) {
                    router.push('/home');
                }
                return false;
            }
            return true;
        });
    }, [router.events]);

    if (!isUserLoggedIn && protectedRoutes.includes(router.pathname)) {
        return (
            <>
                <Head>
                    <link rel="manifest" href="../app.webmainfest" />
                    <meta name="theme-color" content="#317EFB" />
                    <link
                        rel="icon"
                        type="image/gif/png"
                        href="/billzy.png"
                    ></link>
                    <link
                        href="https://fonts.googleapis.com/css?family=Roboto"
                        rel="stylesheet"
                        type="text/css"
                    ></link>
                    <meta
                        name="viewport"
                        content="width=device-width, initial-scale=1, maximum-scale=1"
                    ></meta>
                    <script
                        type="text/javascript"
                        src="/sw-register.js"
                    ></script>
                </Head>
                {isUserLoggedIn == null ? (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100vh',
                        }}
                    >
                        <Image
                            className="loader-gif"
                            src={loaderGIF}
                            alt="loader-image"
                        ></Image>
                    </div>
                ) : (
                    <AccessDenied />
                )}
            </>
        );
    }
    const Boundary = ErrorBoundary || React.Fragment;

    return (
        <Boundary>
            <div className="appContainer">
                <Head>
                    <link rel="manifest" href="../app.webmainfest" />
                    <meta name="theme-color" content="#317EFB" />
                    <link rel="icon" type="image/png" href="/billzy.png"></link>
                    <link
                        href="https://fonts.googleapis.com/css?family=Roboto"
                        rel="stylesheet"
                        type="text/css"
                    ></link>
                    <meta
                        name="viewport"
                        content="width=device-width, initial-scale=1, maximum-scale=1"
                    ></meta>
                    <script
                        type="text/javascript"
                        src="/sw-register.js"
                    ></script>
                </Head>
                <LoadingBar color="#f11946" ref={ref} />
                {Component.staticName != 'Custom404' &&
                Component.staticName != 'errorPage' ? (
                    <Layout
                        isMobile={isMobile}
                        isStandaoloneApp={isStandaoloneApp}
                    >
                        <Provider store={store}>
                            <Component
                                {...pageProps}
                                hideTopLoader={hideTopLoader}
                                showTopLoader={showTopLoader}
                                isMobile={isMobile}
                            />
                        </Provider>
                    </Layout>
                ) : (
                    <Component
                        {...pageProps}
                        hideTopLoader={hideTopLoader}
                        showTopLoader={showTopLoader}
                        isMobile={isMobile}
                    />
                )}
            </div>
        </Boundary>
    );
}
export default wrapper.withRedux(MyApp);
